import {
  Ruler,
  Layers,
  Box,
  Wrench,
  Home,
  Crosshair,
  Link2,
  Hammer,
  Sparkles,
  LayoutGrid,
  Gauge,
  Package,
  Archive,
  RotateCw,
  Wine,
  CornerUpRight,
  MoveVertical,
  Trash2,
  UtensilsCrossed,
  Columns,
  Droplets,
} from "lucide-react";

export const TRUST_STRIP = [
  { icon: Ruler, label: "Precision Manufacturing" },
  { icon: Layers, label: "Quality Materials" },
  { icon: Box, label: "Engineered Carcass" },
  { icon: Wrench, label: "Premium Hardware" },
  { icon: Home, label: "Built for Indian Homes" },
];

export const MARQUEE_ITEMS = [
  "Precision Manufacturing",
  "Engineered Carcass",
  "Quality Materials",
  "Premium Hardware",
  "Built for Indian Homes",
  "Adhesive-Bonded Joinery",
];

export const CONSTRUCTION_POINTS = [
  "Carefully selected plywood",
  "Appropriate board thicknesses",
  "Adhesive bonding",
  "Hydraulic cold pressing",
  "Precision panel cutting",
  "Edge banding",
  "High-grade stainless-steel screws",
  "Strong carcass construction",
  "Water-resistant protection",
  "Premium hardware & accessories",
];

export const QUALITY_PILLARS = [
  {
    icon: Crosshair,
    name: "Precision",
    text: "Accurate cutting and carefully controlled assembly.",
  },
  {
    icon: Layers,
    name: "Materials",
    text: "Carefully selected plywood, laminates and fittings for every application.",
  },
  {
    icon: Link2,
    name: "Bonding",
    text: "Adhesive bonding designed to create secure panel connections.",
  },
  {
    icon: Hammer,
    name: "Joinery",
    text: "High-grade stainless-steel screws combined with adhesive bonding where appropriate.",
  },
  {
    icon: Sparkles,
    name: "Finishing",
    text: "Edge treatment and finishing for a clean, protected appearance.",
  },
];

export const PROCESS_STEPS = [
  {
    n: "01",
    name: "Material Selection",
    text: "We begin by selecting materials appropriate for different applications within the kitchen — plywood in 19 mm, 16 mm and 9 mm thicknesses, chosen according to each component.",
    img: "https://images.pexels.com/photos/5089122/pexels-photo-5089122.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    n: "02",
    name: "Panel Preparation",
    text: "8 × 4 ft plywood sheets are inspected and prepared for production, with every panel planned according to its role in the kitchen.",
    img: "https://images.pexels.com/photos/5089123/pexels-photo-5089123.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    n: "03",
    name: "Lamination",
    text: "Plywood components are laminated with 0.8 mm laminate using adhesive bonding to create a strong, stable construction.",
    img: "https://images.unsplash.com/photo-1787422429897-ad354751e61c?auto=format&fit=crop&w=1200&q=80",
  },
  {
    n: "04",
    name: "Hydraulic Cold Pressing",
    text: "Laminated plywood sheets are kept overnight under a 50-ton automatic hydraulic cold press machine designed specifically for laminating and pressing — helping achieve consistent bonding and a stable finished panel.",
    img: "https://static.prod-images.emergentagent.com/jobs/9b9e0cf7-1501-4059-8d20-4c158267631b/images/233e183a1f6aee85ca10ff40167b1969944f796cd0c0323ce3a0a5aecbd09f0e.jpeg",
  },
  {
    n: "05",
    name: "Precision Panel Cutting",
    text: "Panels are precisely cut on our T3200-M Classic professional panel saw to achieve accurate dimensions and clean cutting, minimising chipping and improving dimensional accuracy.",
    img: "https://static.prod-images.emergentagent.com/jobs/9b9e0cf7-1501-4059-8d20-4c158267631b/images/66914741049f853449c6688ad68a743c1001dddbddc2b0dbacdcda2c9ae034c9.jpeg",
  },
  {
    n: "06",
    name: "Edge Banding",
    text: "Exposed panel edges are processed through an edge-banding machine for a clean, protected and finished edge.",
    img: "https://static.prod-images.emergentagent.com/jobs/9b9e0cf7-1501-4059-8d20-4c158267631b/images/846889d9c1366c15d0a143d563096037c7eb37925c50ec27448e600c04dbb175.jpeg",
  },
  {
    n: "07",
    name: "Carcass Assembly",
    text: "Carcasses are assembled using adhesive bonding and high-grade stainless-steel screws, with carefully aligned components and secure structural joining.",
    img: "https://static.prod-images.emergentagent.com/jobs/9b9e0cf7-1501-4059-8d20-4c158267631b/images/fd9809ffd4788ecec2fc692deaa74ac91251e99febf2bd663ab4d5158052d90a.jpeg",
  },
  {
    n: "08",
    name: "Hardware Installation",
    text: "Soft-close hinges, drawer systems and fittings from established brands are installed and carefully calibrated.",
    img: "https://static.prod-images.emergentagent.com/jobs/9b9e0cf7-1501-4059-8d20-4c158267631b/images/3a1a83799a53183a32f9c66d52dda56e4f7b7352809397aec4ebb601e21d5ee5.jpeg",
  },
  {
    n: "09",
    name: "Quality Inspection",
    text: "Every component is inspected for alignment, finish and smooth operation before it leaves our workshop.",
    img: "https://images.unsplash.com/photo-1659930087003-2d64e33181f7?auto=format&fit=crop&w=1200&q=80",
  },
  {
    n: "10",
    name: "Installation",
    text: "Your kitchen is installed on site with attention to alignment, levelling and finishing detail — so it performs beautifully every day.",
    img: "https://images.unsplash.com/photo-1663811396777-05505d999151?auto=format&fit=crop&w=1200&q=80",
  },
];

export const MATERIALS = [
  {
    name: "Plywood",
    spec: "19 mm · 16 mm · 9 mm",
    text: "The structural core of every kitchen. Plywood is selected in thicknesses appropriate to each component and application, with 8 × 4 ft sheets used for production.",
    chips: ["19 mm", "16 mm", "9 mm"],
  },
  {
    name: "WPC",
    spec: "Base-unit protection",
    text: "A high-grade WPC sheet is incorporated on top and protective areas of base units, helping protect the structure from accidental water seepage from the stone countertop.",
    chips: ["High-grade", "Protective areas"],
  },
  {
    name: "Laminates",
    spec: "0.8 mm",
    text: "0.8 mm decorative laminate bonded to both faces of every panel for a durable, easy-to-clean surface finish.",
    chips: ["0.8 mm", "Both faces"],
  },
  {
    name: "Adhesives",
    spec: "High-grade bonding",
    text: "High-grade adhesive for lamination and structural bonding — the hidden layer behind stable panels and secure assemblies.",
    chips: ["Lamination", "Structural"],
  },
  {
    name: "Edge Banding",
    spec: "Machine-applied",
    text: "Every exposed board edge is processed through an edge-banding machine, protecting the core and completing the appearance of the component.",
    chips: ["Protected edges", "Clean finish"],
  },
  {
    name: "Hardware",
    spec: "Established brands",
    text: "Hinges, drawer systems and fittings from established brands — selected according to project requirements and availability.",
    chips: ["Soft-close", "Premium"],
  },
  {
    name: "Screws",
    spec: "Stainless steel",
    text: "High-grade stainless-steel screws used for structural assembly, combined with adhesive bonding depending on component and application.",
    chips: ["High-grade", "Structural"],
  },
  {
    name: "Hinges",
    spec: "Soft-close · 3D adjustable",
    text: "Soft-close and 3D adjustable hinge options so every door closes quietly and sits perfectly aligned.",
    chips: ["Soft-close", "3D adjustable"],
  },
  {
    name: "Drawer Systems",
    spec: "Tandem & soft-close",
    text: "Premium tandem and soft-close drawer systems for smooth, organised storage that works as beautifully as it looks.",
    chips: ["Tandem", "Soft-close"],
  },
];

export const ACCESSORY_CATEGORIES = [
  { icon: LayoutGrid, name: "Drawer Systems" },
  { icon: Link2, name: "Hinges" },
  { icon: Gauge, name: "Soft-Close Systems" },
  { icon: Package, name: "Pull-Out Baskets" },
  { icon: Archive, name: "Pantry Units" },
  { icon: RotateCw, name: "Magic Corner Systems" },
  { icon: Layers, name: "Tandem Drawers" },
  { icon: Wine, name: "Bottle Pull-Outs" },
  { icon: CornerUpRight, name: "Corner Solutions" },
  { icon: MoveVertical, name: "Lift-Up Systems" },
  { icon: Trash2, name: "Waste Management" },
  { icon: UtensilsCrossed, name: "Cutlery Systems" },
  { icon: Columns, name: "Tall-Unit Storage" },
  { icon: Droplets, name: "Under-Sink Storage" },
];

export const HARDWARE_HIGHLIGHTS = [
  "Soft-close hinges",
  "3D adjustable hinges",
  "Premium drawer systems",
  "Pull-out mechanisms",
  "High-quality baskets",
  "Organised storage accessories",
];

export const JOURNEY_STEPS = [
  { n: "01", name: "Consultation", text: "Share your requirements, ideas and budget with our team." },
  { n: "02", name: "Site Measurement", text: "We measure your space carefully — every dimension matters." },
  { n: "03", name: "Design & Material Selection", text: "Layout, finishes and hardware selected together with you." },
  { n: "04", name: "Manufacturing", text: "Your kitchen is engineered in our workshop, step by step." },
  { n: "05", name: "Installation", text: "Installed, aligned and finished — ready for everyday life." },
];

export const BRANDS = ["Hettich", "Häfele", "Blum", "Ebco", "Sleek"];

export const STATS = [
  { value: 50, suffix: "", label: "Ton hydraulic cold press" },
  { value: 3, suffix: "", label: "Board thicknesses — 19/16/9 mm" },
  { value: 10, suffix: "", label: "Manufacturing steps" },
  { value: 20, suffix: "", label: "Kitchen designs to explore" },
];
